/**
 * SourcesSheet — the non-modal floating Sources panel (CR §3.4, Track 3 §5.1).
 *
 * Two sections:
 *   • **Input documents** — the SOW and presales transcripts. Upload (presigned S3 PUT) and delete,
 *     reusing `Phase0Panel`'s logic verbatim: request a presigned URL from
 *     `POST /phase0/upload`, then PUT the bytes straight to S3 *without* the app Authorization
 *     header (the presigned signature is the credential; sending both makes S3 reject the request).
 *   • **Generated docs** — the five artifacts. Clicking one opens it as a file tab, which is the
 *     panel's whole reason for existing: it is the file picker for an IDE-style workspace.
 *
 * ── WHY NON-MODAL IS A REQUIREMENT, NOT A PREFERENCE ───────────────────────────────────────────
 * The surface this replaces was a blocking modal, which forced the user to choose between consulting
 * their sources and typing an instruction about them. `modal={false}` plus `overlay={false}` means
 * the chat behind it stays interactive — CR §3.4: "Non-modal floating panel — stays open alongside
 * work." Both flags are needed: Radix's `modal={false}` stops focus trapping and pointer blocking at
 * the layer level, but `SheetOverlay` is `fixed inset-0` and would still swallow every click.
 * `onInteractOutside` is prevented for the same reason — a panel that closes the moment you click
 * the thing you opened it to work on is a modal with extra steps. Escape still closes it.
 *
 * ── 🟢 SEC-4 IS CLOSED SERVER-SIDE — THIS FILE IS STILL ONLY A UX GUARD ────────────────────────
 * CR §6.1 SEC-4 required upload validation to be a **server-side allowlist**.
 * `packages/projects/handlers/phase0-upload.ts` now enforces one: an extension + content-type
 * allowlist (400 `UNSUPPORTED_FILE_TYPE`), a 100 MB cap (400 `FILE_TOO_LARGE`), and — the part that
 * makes the first two binding rather than advisory — `content-type` and `content-length` are **signed
 * headers** on the presigned PUT, so S3 itself rejects a request that deviates. Read that handler's
 * docstring before changing anything here.
 *
 * The pre-check below is **retained deliberately as a second layer**, not because it is a control. It
 * is trivially bypassable — any client can call the endpoint directly — and must still not be mistaken
 * for the mitigation. Its job is immediate feedback instead of a confusing S3 signature failure.
 * Keeping both layers is the point; do not delete this one on the grounds that the server now checks.
 *
 * One residual gap survives and is **not** closed by either layer: there is no magic-byte
 * verification anywhere, because the bytes stream client → S3 and never pass through a Lambda. See the
 * handler docstring and the CR §6.1 SEC-4 row.
 */

import { useId, useRef, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  FileText,
  Link2,
  Loader2,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useApiClient } from "@/lib/api";
import { toastIfNotProjectMember } from "@/lib/project-permissions";
import {
  describePhase0LinkError,
  postPhase0Link,
  sharePointLinkError,
  SHAREPOINT_LINK_ACCEPTED_MESSAGE,
  SHAREPOINT_LINK_PLACEHOLDER,
  type DocsSyncStatus,
} from "@/lib/sharepoint-link";
import {
  ARTIFACT_LABELS,
  type ArtifactsState,
  type ArtifactType,
} from "@/hooks/useArtifacts";

/**
 * Row badge copy per lifecycle status. The panel is the file picker, so "which of these is
 * committed" is the question a user opens it to answer (§4.2).
 */
const ARTIFACT_STATUS_LABELS: Record<string, string> = {
  generated: "Generated",
  draft_edit: "Draft",
  pending_commit: "Pending approval",
  approving: "Approving",
  committed: "Committed",
  rejected: "Rejected",
};

/**
 * Client-side pre-check allowlist — UX only, see the SEC-4 note above.
 *
 * **Must mirror `UPLOAD_ALLOWLIST` in `packages/projects/services/upload-validation.service.ts`.** If
 * this list is wider than the server's, the user clears the pre-check and then gets a 400, which is
 * worse than no pre-check at all. (It previously listed `.rtf`, `.vtt` and `.csv`, which the server
 * allowlist does not include.)
 */
const ACCEPTED_EXTENSIONS = [
  ".aspx",
  ".doc",
  ".docx",
  ".md",
  ".pdf",
  ".ttf",
  ".txt",
  ".xlsx",
] as const;
export const MAX_UPLOAD_BYTES = 104_857_600; // 100 MB — matches MAX_UPLOAD_BYTES on the server

/** `DELETE /phase0/files/{filename}` response (specs/api/projects.yaml). */
interface DeleteFileResponse {
  deleted?: string;
  /** True when an active generation that read this file was stopped. */
  generation_stopped?: boolean;
  stopped_job_ids?: string[];
  /** Set when the file was deleted but stopping a running generation failed. */
  warning?: string;
}

function errorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as AxiosError<{ message?: string }>;
  return (
    axiosErr?.response?.data?.message ??
    (err instanceof Error ? err.message : fallback)
  );
}

/** Returns a user-facing rejection reason, or `null` if the file passes the pre-check. */
function preCheckRejection(file: File): string | null {
  const lower = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return `${file.name} — unsupported file type. Accepted: ${ACCEPTED_EXTENSIONS.join(", ")}`;
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `${file.name} — larger than 100 MB`;
  }
  return null;
}

export interface SourcesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  uploadedFiles: string[];
  /**
   * Write controls (upload, delete, add-from-link) are hidden unless the viewer's role allows them
   * AND they are on the project's team (leadership/admin bypass). The backend re-enforces both.
   */
  canManage: boolean;
  /**
   * `phase0/status.docs_sync_status` — download state of the latest SharePoint link sync. Drives
   * the "Downloading from SharePoint…" / failure line under the inputs. null/undefined = none.
   */
  docsSyncStatus?: DocsSyncStatus | null;
  /**
   * Three-phase artifact existence (`ArtifactsState`, §3.3.1). Replaces
   * `hasGeneratedArtifacts: boolean`.
   *
   * Derived from the five per-artifact GETs, NOT from `phase0_generations.status`: a failed
   * regeneration leaves the previous run's artifacts intact, and a chat-accepted edit never writes
   * to `phase0_generations`. Gating on the job status made both cases read as "nothing generated
   * yet".
   *
   * `'loading'` renders a skeleton. `'error'` renders a skeleton plus a Retry link. ONLY `'ready'`
   * with an empty `existing` may render the empty copy.
   */
  artifacts: ArtifactsState;
  /** Per-artifact lifecycle status, for the row badges. Complete only in the `'ready'` phase. */
  artifactStatuses: Record<string, string>;
  /** Opens the clicked artifact as a file tab. */
  onOpenArtifact: (artifactType: ArtifactType) => void;
  /** Artifacts already open, so the panel can mark them rather than implying a second copy opens. */
  openArtifacts: ArtifactType[];
}

export function SourcesSheet({
  open,
  onOpenChange,
  projectId,
  uploadedFiles,
  canManage,
  docsSyncStatus = null,
  artifacts,
  artifactStatuses,
  onOpenArtifact,
  openArtifacts,
}: SourcesSheetProps): JSX.Element {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // ── Add from SharePoint link (POST /phase0/link) ──
  const linkInputId = useId();
  const linkErrorId = useId();
  const [link, setLink] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);

  const addLink = useMutation({
    mutationFn: (url: string) => postPhase0Link(client, projectId, url),
    onSuccess: () => {
      toast.success(SHAREPOINT_LINK_ACCEPTED_MESSAGE);
      setLink("");
      setLinkError(null);
      void queryClient.invalidateQueries({
        queryKey: ["phase0-status", projectId],
      });
    },
    onError: (err) => {
      const described = describePhase0LinkError(err);
      if (described.kind === "field") {
        setLinkError(described.message);
        return;
      }
      if (toastIfNotProjectMember(err)) return;
      toast.error(described.message);
      // 409 LINK_SYNC_IN_PROGRESS — pick up the in-flight sync's state.
      void queryClient.invalidateQueries({
        queryKey: ["phase0-status", projectId],
      });
    },
  });

  function handleLinkSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (addLink.isPending) return;
    const error = sharePointLinkError(link);
    setLinkError(error);
    if (error) return;
    addLink.mutate(link.trim());
  }

  const isLinkSyncing =
    docsSyncStatus === "pending" || docsSyncStatus === "syncing";

  const deleteFile = useMutation({
    mutationFn: async (filename: string): Promise<DeleteFileResponse> => {
      const response = await client.delete<DeleteFileResponse>(
        `/api/projects/${projectId}/phase0/files/${encodeURIComponent(filename)}`,
      );
      return response?.data ?? {};
    },
    onSuccess: (data) => {
      // Deleting a file that a running generation is reading stops that run server-side, so its
      // results never land. Say so — otherwise the run appears to vanish for no reason.
      if (data.generation_stopped) {
        toast.info(
          "File removed — generation stopped because a source file was deleted",
        );
      } else {
        toast.success("File removed");
      }
      if (data.warning) toast.warning(data.warning);
      void queryClient.invalidateQueries({
        queryKey: ["phase0-status", projectId],
      });
    },
    onError: (err) => {
      if (!toastIfNotProjectMember(err))
        toast.error(errorMessage(err, "Failed to remove file"));
    },
  });

  /** Upload each selected file: request a presigned URL, then PUT the bytes directly to S3. */
  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;

    const selected = Array.from(files);
    const rejections = selected
      .map(preCheckRejection)
      .filter((r): r is string => r !== null);
    if (rejections.length > 0) {
      rejections.forEach((reason) => toast.error(reason));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      for (const file of selected) {
        const { data } = await client.post<{
          upload_url: string;
          s3_key: string;
          content_type: string;
        }>(`/api/projects/${projectId}/phase0/upload`, {
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        });
        // Direct S3 PUT — must NOT carry the app Authorization header (uses the presigned signature).
        // `Content-Type` must be the server's canonical value, not `file.type`: it is a signed header,
        // so any deviation is a SignatureDoesNotMatch. `Content-Length` is signed too, but it is a
        // forbidden header for fetch — the browser sets it from the File body, matching size_bytes.
        const put = await fetch(data.upload_url, {
          method: "PUT",
          headers: { "Content-Type": data.content_type },
          body: file,
        });
        if (!put.ok)
          throw new Error(`Upload failed for ${file.name} (${put.status})`);
      }
      toast.success(
        selected.length === 1
          ? "Document uploaded"
          : `${selected.length} documents uploaded`,
      );
      void queryClient.invalidateQueries({
        queryKey: ["phase0-status", projectId],
      });
    } catch (err) {
      if (!toastIfNotProjectMember(err))
        toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        // See the non-modal note in this file's docstring — both of these are load-bearing.
        overlay={false}
        onInteractOutside={(e) => e.preventDefault()}
        // Width is NOT set here: `sm:max-w-xl` is the shared `right`-side default in sheet.tsx so
        // this panel and the weekly-report panel cannot drift apart. Modality above is untouched.
        className="flex w-full flex-col gap-4 overflow-y-auto"
        aria-describedby="sources-sheet-description"
      >
        <SheetHeader>
          <SheetTitle>Sources</SheetTitle>
          <SheetDescription id="sources-sheet-description">
            Inputs the agents read, and the documents they produced. This panel
            stays open while you work.
          </SheetDescription>
        </SheetHeader>

        {/* ── Input documents ── */}
        <section aria-label="Input documents" className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-text-primary">
              Input documents
            </h3>
            {canManage && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept={ACCEPTED_EXTENSIONS.join(",")}
                  aria-label="Select SOW or transcript files"
                  onChange={(e) => void handleFiles(e.target.files)}
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  ) : (
                    <Upload className="h-4 w-4" aria-hidden="true" />
                  )}
                  Upload
                </Button>
              </>
            )}
          </div>

          {canManage && (
            <form
              onSubmit={handleLinkSubmit}
              noValidate
              aria-label="Add a document from a SharePoint link"
              className="flex flex-col gap-1"
            >
              <Label
                htmlFor={linkInputId}
                className="text-xs font-medium text-text-secondary"
              >
                SharePoint link
              </Label>
              <div className="flex items-start gap-2">
                <Input
                  id={linkInputId}
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  placeholder={SHAREPOINT_LINK_PLACEHOLDER}
                  value={link}
                  onChange={(e) => {
                    setLink(e.target.value);
                    if (linkError) setLinkError(null);
                  }}
                  aria-invalid={linkError ? true : undefined}
                  aria-describedby={linkError ? linkErrorId : undefined}
                  className="h-9 min-w-0 flex-1"
                />
                <Button
                  type="submit"
                  variant="outline"
                  size="sm"
                  disabled={addLink.isPending}
                >
                  {addLink.isPending ? (
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  ) : (
                    <Link2 className="h-4 w-4" aria-hidden="true" />
                  )}
                  Add from link
                </Button>
              </div>
              {linkError && (
                <p id={linkErrorId} className="text-xs text-error" role="alert">
                  {linkError}
                </p>
              )}
            </form>
          )}

          {/* Download state of the latest SharePoint link sync. The live region is always mounted so
              a change from pending → failed is announced; `synced` adds nothing — the file simply
              appears in the list below. */}
          <div role="status" aria-live="polite">
            {isLinkSyncing && (
              <p className="flex items-center gap-2 text-sm text-text-secondary">
                <Loader2
                  className="h-4 w-4 shrink-0 animate-spin"
                  aria-hidden="true"
                />
                Downloading from SharePoint…
              </p>
            )}
            {docsSyncStatus === "failed" && (
              <p className="flex items-start gap-2 text-sm text-error">
                <AlertCircle
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
                <span>
                  Couldn&apos;t download the document from SharePoint. Check the
                  link is shared with the organisation, or upload the file
                  instead.
                </span>
              </p>
            )}
          </div>

          {uploadedFiles.length === 0 ? (
            <p className="text-sm text-text-secondary">
              No documents uploaded yet
            </p>
          ) : (
            <ul className="divide-y divide-outlines rounded-lg border border-outlines">
              {uploadedFiles.map((f) => (
                <li
                  key={f}
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                >
                  <FileText
                    className="h-4 w-4 shrink-0 text-text-secondary"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate text-text-primary">
                    {f}
                  </span>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => deleteFile.mutate(f)}
                      disabled={deleteFile.isPending}
                      aria-label={`Remove ${f}`}
                      className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded text-text-secondary transition-colors hover:bg-badge-red-bg hover:text-error"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ── Generated docs ── */}
        <section
          aria-label="Generated documents"
          className="flex flex-col gap-2"
        >
          <h3 className="text-sm font-medium text-text-primary">
            Generated docs
          </h3>

          {/* ── Item 3 (§4.2) — three branches, driven by artifact EXISTENCE ──────────────────────
              This section used to be gated on `hasGeneratedArtifacts`
              (= `generation?.status === 'complete'`), which is a statement about the latest JOB.
              The reported defect reproduces exactly from that: the user's latest generate run
              failed, so the flag was false, every `artifact_edits` row from the earlier successful
              run still existed, the Preliminary SRS opened as a tab and rendered content — and this
              panel said "Nothing generated yet". Two statements from different sources, and this
              one was the wrong one.

              It also listed all five `ARTIFACT_TYPES` unconditionally inside the truthy branch, so
              the inverse error shipped too: after a partial regeneration, clicking a type that was
              skipped opened a tab showing "has not been generated yet".

              ONLY `'ready'` with an empty `existing` may render the empty copy. Rendering it in
              either unresolved phase would re-display the exact sentence this defect was reported
              as — and in the ERROR phase it would do so PERMANENTLY, because `useArtifact` sets
              `retry: false`, which would read as the bug being unfixed rather than as a load
              failure.

              `role="status"` IS correct here, unlike in `ChatSystemPrompt`: the sheet is not a
              descendant of the transcript's `role="log"`, so there is no nested live region. */}
          {artifacts.phase === "loading" ? (
            <div role="status" aria-label="Loading generated documents">
              <Skeleton className="h-24 w-full" />
            </div>
          ) : artifacts.phase === "error" ? (
            <div role="status" className="space-y-2">
              <div aria-hidden="true">
                <Skeleton className="h-24 w-full" />
              </div>
              <p className="text-sm text-text-secondary">
                Couldn&apos;t load the generated documents.{" "}
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={artifacts.retry}
                >
                  Retry
                </Button>
              </p>
            </div>
          ) : artifacts.existing.length === 0 ? (
            // Design-system empty state (Task #7). Copy points at the one generation entry point
            // and the one place that explains it (§3.5) — not at a control that no longer exists.
            <EmptyState
              className="py-6"
              icon={<Sparkles className="h-5 w-5" aria-hidden="true" />}
              title="Nothing generated yet"
              description="Once you have uploaded your sources, use the prompt in the chat to generate all five artifacts."
            />
          ) : (
            <ul className="divide-y divide-outlines rounded-lg border border-outlines">
              {artifacts.existing.map((artifactType) => {
                const isOpen = openArtifacts.includes(artifactType);
                const status = artifactStatuses[artifactType];
                return (
                  <li key={artifactType}>
                    <button
                      type="button"
                      onClick={() => onOpenArtifact(artifactType)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-subtle-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
                    >
                      <Sparkles
                        className="h-4 w-4 shrink-0 text-text-secondary"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1 truncate text-text-primary">
                        {ARTIFACT_LABELS[artifactType]}
                      </span>
                      {/* The panel is the file picker, and "which of these is committed" is the
                          question a user opens it to answer. */}
                      {status && (
                        <span className="shrink-0 text-xs text-text-secondary">
                          {ARTIFACT_STATUS_LABELS[status] ?? status}
                        </span>
                      )}
                      <span className="shrink-0 text-xs text-text-secondary">
                        {isOpen ? "Open" : "Open in tab"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </SheetContent>
    </Sheet>
  );
}

export default SourcesSheet;
